import { useCallback, useRef, useState } from 'react';
import {
  FullscreenOverlayBackdrop,
  FullscreenOverlayBody,
  FullscreenOverlayContent,
  FullscreenOverlayFooter,
  FullscreenOverlayPortal,
  FullscreenOverlayRoot,
} from '../../../../ui/FullscreenOverlay';
import { SpokeFooterCTAs } from '../../SpokeFooterCTAs';
import { ListingHeaderActions } from '../../ListingHeaderActions';
import { HaloSpokeHeader } from '../../HaloSpokeHeader';
import { buildUnitSpecs, UnitDetailSpokeContent } from './UnitDetailSpokeContent';
import type { Floorplan, Unit } from '../UnitTable';
import * as styles from './unitDetailOverlay.styles';

type UnitDetailOverlayProps = {
  unit: Unit;
  floorplan: Floorplan;
  open: boolean;
  onClose: () => void;
};

export const UnitDetailOverlay = ({ unit, floorplan, open, onClose }: UnitDetailOverlayProps) => {
  const [summaryHidden, setSummaryHidden] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const specs = buildUnitSpecs(unit);

  const sentinelRef = useCallback((node: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    setSummaryHidden(false);

    if (!node) return;

    observerRef.current = new IntersectionObserver(([entry]) => setSummaryHidden(!entry.isIntersecting), {
      threshold: 0,
    });
    observerRef.current.observe(node);
  }, []);

  return (
    <FullscreenOverlayRoot open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <FullscreenOverlayPortal>
        <FullscreenOverlayBackdrop closeOnBackdropClick={false}>
          <FullscreenOverlayContent variant="standard" ariaLabelledBy="unit-detail-title">
            <HaloSpokeHeader
              title={`Unit ${unit.name}`}
              subtitle={summaryHidden ? specs : undefined}
              showCloseButton
              layout="sticky"
              variant="standard"
              actions={
                <div className={styles.headerRight}>
                  <ListingHeaderActions />
                </div>
              }
            />

            <FullscreenOverlayBody padding="standard" variant="standard">
              <UnitDetailSpokeContent unit={unit} floorplan={floorplan} sentinelRef={sentinelRef} />
            </FullscreenOverlayBody>

            <FullscreenOverlayFooter variant="equal-width">
              <div className={styles.footerActions}>
                <SpokeFooterCTAs />
              </div>
            </FullscreenOverlayFooter>
          </FullscreenOverlayContent>
        </FullscreenOverlayBackdrop>
      </FullscreenOverlayPortal>
    </FullscreenOverlayRoot>
  );
};
